import { css } from '@styled-system/css';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import {
  BestMomentsHeatmap,
  type WeeklySlotPoint,
} from '@/features/analysisPageStatistics/components/BestMomentsHeatmap';
import {
  CharacteristicCompare,
  type CharacteristicStat,
} from '@/features/analysisPageStatistics/components/CharacteristicCompare';
import {
  DailyActivityChart,
  type DailyActivityPoint,
} from '@/features/analysisPageStatistics/components/DailyActivityChart';
import { PriceDistributionBars } from '@/features/analysisPageStatistics/components/PriceDistributionBars';
import { SaleTimeDistributionBars } from '@/features/analysisPageStatistics/components/SaleTimeDistributionBars';
import type { AnalysisStatisticsData } from '@/features/analysisPageStatistics/types';
import { type SaleTimeBucket, computeRepostTimeHours, formatHoursShort } from '@/shared/utils/analysis/saleTime';
import { formatNumber, formatPrice, formatTime } from '@/shared/utils/format';

// ============================================================================
// Onglet Statistiques — deux cards de graphe avec la stat clé intégrée à
// gauche : répartition des prix (+ prix moyen en gros) et activité
// quotidienne (+ nouveaux produits/jour). Props-driven, partagé.
// ============================================================================

const panel = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '8',
});

const section = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
});

const sectionTitle = css({
  fontSize: 'micro',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  fontWeight: 'medium',
});

// Card de graphe : bloc stat à gauche (bordure de séparation), graphe à droite.
const chartCard = css({
  display: 'flex',
  alignItems: 'stretch',
  gap: '8',
  paddingY: '6',
  paddingX: '6',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  flexWrap: 'wrap',
});

// Bloc stat de gauche — panneau inset sur fond subtle pour ressortir du graphe.
const statBlock = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '4',
  width: '220px',
  flexShrink: 0,
  paddingY: '6',
  paddingX: '6',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  // Sur écran étroit, le bloc passe au-dessus du graphe en pleine largeur.
  '@media (max-width: 720px)': {
    width: '100%',
  },
});

const statLabel = css({
  fontSize: 'caption',
  color: 'text.tertiary',
});

const statValue = css({
  fontFamily: 'mono',
  fontSize: '28px',
  lineHeight: '1.1',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.8px',
});

const statSubRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '2',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const subDot = css({
  width: '8px',
  height: '8px',
  borderRadius: 'full',
  flexShrink: 0,
});

const subPrice = css({
  fontFamily: 'mono',
  fontWeight: 'semibold',
  color: 'text.primary',
});

const chartSlot = css({
  flex: 1,
  minWidth: '320px',
});

// ============================================================================

export interface AnalysisStatsPanelProps {
  data: AnalysisStatisticsData | undefined;
  loading?: boolean;
  /** Série quotidienne ventes/ajouts — mock tant que les snapshots backend n'existent pas. */
  dailyActivity?: DailyActivityPoint[] | null;
  /** Pattern hebdomadaire jour × créneau — mock tant que l'agrégat backend n'existe pas. */
  weeklyPattern?: WeeklySlotPoint[] | null;
  /** Comparaison par caractéristique (états, tailles) — mock idem. */
  characteristics?: { conditions: CharacteristicStat[]; sizes: CharacteristicStat[] } | null;
  /** Histogramme des temps de vente — mock tant que l'agrégat backend n'existe pas. */
  saleTimeDistribution?: SaleTimeBucket[] | null;
  /** 'vendor' : une seule heatmap (mise en vente), pas de comparaison. */
  variant?: 'product' | 'vendor';
}

export const AnalysisStatsPanel: FC<AnalysisStatsPanelProps> = ({
  data,
  loading,
  dailyActivity,
  weeklyPattern,
  characteristics,
  saleTimeDistribution,
  variant = 'product',
}) => {
  const { t } = useTranslation();

  const q1 = data?.priceDistributionQ1 ?? null;
  const q3 = data?.priceDistributionQ3 ?? null;
  // Repost = P80 : le délai sous lequel 80 % des ventes sont réalisées
  const repostHours = computeRepostTimeHours(saleTimeDistribution ?? null);

  return (
    <div className={panel}>
      {/* ─── Répartition des prix ─────────────────────────────────────── */}
      <div className={section}>
        <span className={sectionTitle}>{t('statsPanel.distributionTitle')}</span>
        <div className={chartCard}>
          <div className={statBlock}>
            <span className={statLabel}>{t('statsPanel.avgPrice')}</span>
            <span className={statValue}>
              {loading ? <Skeleton width={110} /> : formatPrice(data?.averageSalePrice ?? null)}
            </span>
            <div className={statSubRow}>
              <span className={subDot} style={{ background: 'var(--colors-warning-default)' }} />
              {t('statsPanel.markerBuy')}
              <span className={subPrice}>{loading ? <Skeleton width={40} /> : formatPrice(q1)}</span>
            </div>
            <div className={statSubRow}>
              <span className={subDot} style={{ background: 'var(--colors-success-default)' }} />
              {t('statsPanel.markerSell')}
              <span className={subPrice}>{loading ? <Skeleton width={40} /> : formatPrice(q3)}</span>
            </div>
          </div>
          <div className={chartSlot}>
            <PriceDistributionBars
              buckets={data?.priceDistributionBuckets ?? []}
              priceMin={data?.priceDistributionMin ?? null}
              q1={q1}
              q3={q3}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {/* ─── Activité quotidienne ─────────────────────────────────────── */}
      <div className={section}>
        <span className={sectionTitle}>{t('statsPanel.activityTitle')}</span>
        <div className={chartCard}>
          <div className={statBlock}>
            <span className={statLabel}>{t('statsPanel.newPerDay')}</span>
            <span className={statValue}>
              {loading ? <Skeleton width={80} /> : formatNumber(data?.newProductsPerDay ?? null)}
            </span>
          </div>
          <div className={chartSlot}>
            <DailyActivityChart data={dailyActivity ?? null} loading={loading} />
          </div>
        </div>
      </div>

      {/* ─── Répartition des temps de vente (produit uniquement) ──────── */}
      {variant === 'product' && (
        <div className={section}>
          <span className={sectionTitle}>{t('statsPanel.saleTimeTitle')}</span>
          <div className={chartCard}>
            <div className={statBlock}>
              <span className={statLabel}>{t('statsPanel.avgSaleTime')}</span>
              <span className={statValue}>
                {loading ? <Skeleton width={110} /> : formatTime(data?.averageSaleTimeSec ?? null, 'min')}
              </span>
              <div className={statSubRow}>
                <span className={subDot} style={{ background: 'var(--colors-success-default)' }} />
                {t('statsPanel.repostTime')}
                <span className={subPrice}>
                  {loading ? <Skeleton width={40} /> : repostHours == null ? '—' : formatHoursShort(repostHours)}
                </span>
              </div>
            </div>
            <div className={chartSlot}>
              <SaleTimeDistributionBars
                buckets={saleTimeDistribution ?? null}
                repostHours={repostHours}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}

      {/* ─── Meilleurs moments ────────────────────────────────────────── */}
      <div className={section}>
        <span className={sectionTitle}>{t('statsPanel.bestMomentsTitle')}</span>
        <div className={chartCard}>
          <div className={chartSlot}>
            <BestMomentsHeatmap data={weeklyPattern ?? null} loading={loading} variant={variant} />
          </div>
        </div>
      </div>

      {/* ─── Comparaison par caractéristique (produit uniquement) ─────── */}
      {variant === 'product' && (
        <div className={section}>
          <span className={sectionTitle}>{t('statsPanel.compareTitle')}</span>
          <div className={chartCard}>
            <div className={chartSlot}>
              <CharacteristicCompare
                conditions={characteristics?.conditions ?? null}
                sizes={characteristics?.sizes ?? null}
                loading={loading}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
